"use client";

import {
  ChevronDown,
  Search,
  MessageCircle,
  ShoppingBag,
  ClipboardCheck,
  CreditCard,
  Star,
  HelpCircle,
} from "lucide-react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import { ProseLink } from "@/components/ProseLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHero } from "@/components/landing/PageHero";
// `import type` — NOT a value import. `./faq-groups` is a pure-data module, but
// keeping this type-only is what stops the erased edge from ever becoming a real
// one (see FAQBUNDLE below).
import type { DisplayFaqGroup } from "./faq-groups";

/**
 * FAQBUNDLE — there is deliberately NO `SEED_GROUPS` default here any more, and
 * `@/lib/faq-defaults` is deliberately NOT imported.
 *
 * `groups` used to default to `resolveFaqGroups(DEFAULT_FAQ_GROUPS, [])`, and that
 * one default parameter was the ONLY value-import edge from this `"use client"`
 * module to `lib/faq-defaults.ts` — **441 lines of static FAQ prose**, shipped in
 * the `/faq` client bundle and then never rendered, because `page.tsx` always
 * passes the server-resolved groups (its own comment said so). `./faq-groups`
 * imports the `FaqGroup` shape `import type`, which is erased, so removing this
 * one line removes the corpus from the browser download entirely.
 *
 * `groups` is now REQUIRED, so `tsc` — not a runtime fallback — is what guarantees
 * every caller supplies them. An optional prop with an empty-array default would
 * have kept the leak closed but silently rendered an empty FAQ for a caller that
 * forgot, which is the worse failure on an indexed page.
 *
 * Guarded by `tests/unit/app/faq-bundle-boundary.spec.tsx`, which mocks
 * `@/lib/faq-defaults` with a factory that records being loaded and asserts the
 * factory never runs when this module is imported.
 */

function normalizeForSearch(text: string) {
  return text.toLowerCase().replace(/\s+/g, " ").trim();
}

function matchesSearch(q: string, a: string, query: string): boolean {
  const normalized = normalizeForSearch(query);
  if (!normalized) return true;
  return (
    normalizeForSearch(q).includes(normalized) ||
    normalizeForSearch(a).includes(normalized)
  );
}

/** Real, aggregated customer proof passed from the server (never fabricated —
 *  rendered only when genuine testimonials exist in R2). */
export interface FaqCustomerProof {
  count: number;
  average: number;
  quote: { text: string; author: string } | null;
}

const ORDER_STEPS = [
  {
    icon: ShoppingBag,
    title: "Browse & add to cart",
    body: "Explore the shop and add the pieces you love to your cart.",
  },
  {
    icon: CreditCard,
    title: "Check out & pay by card",
    body: "Pay securely by card at checkout — Stripe handles payment (Apple Pay & Google Pay too); your card details never touch our site.",
  },
  {
    icon: ClipboardCheck,
    title: "We confirm the details",
    body: "After payment we get in touch to confirm your brief, then handcraft your order.",
  },
];

/**
 * Below this many testimonials the on-page trust badge shows the RATING only
 * ("Rated 5.0 out of 5") and hides the raw count — a tiny "from 3 happy
 * customers" undersells a perfect score. At/above it the count reads as real
 * social proof ("5.0 from N happy customers"). It auto-upgrades as testimonials
 * accrue; adjust the floor to taste. (JSON-LD aggregateRating still reports the
 * true count — schema must stay honest.)
 */
const MIN_PROOF_COUNT = 10;

export function FaqClient({
  customerProof = null,
  groups,
}: {
  customerProof?: FaqCustomerProof | null;
  /**
   * The RESOLVED FAQ groups — admin CMS content merged with the config-driven
   * collection/delivery groups and sorted — passed down from the server page.
   *
   * FAQSSR: this component used to render `DEFAULT_FAQ_GROUPS` server-side and
   * then swap the real CMS content in from a mount `fetch("/api/content/faq")`,
   * while `page.tsx` built the FAQPage JSON-LD from the CMS content it had
   * already fetched. The schema therefore described Q&As that the crawlable HTML
   * did not contain. The server has the groups in hand, so it passes them: the
   * SSR HTML and the schema are now built from one value, and the mount fetch is
   * gone (it duplicated a read the page had already made, and the page's own
   * `revalidate = 300` is the freshness contract the FAQ is held to anyway).
   *
   * REQUIRED (FAQBUNDLE): it used to be optional with a `DEFAULT_FAQ_GROUPS`
   * default, which dragged the whole 441-line seed corpus into the client bundle.
   */
  groups: DisplayFaqGroup[];
}) {
  // Lazy initialisers over the SAME `groups` prop the server rendered, so the
  // first client render matches the SSR HTML exactly — hydration-safe (no
  // cookie/localStorage read, no Date.now(), no post-mount content swap).
  const [open, setOpen] = useState<string | null>(
    () => groups[0]?.sections[0]?.faqs[0]?.id ?? null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState<string>(
    () => groups[0]?.id ?? ""
  );
  const [isReady, setIsReady] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const hashHandled = useRef(false);

  // NOTE: `groups` is used directly everywhere below — deliberately NOT copied
  // into local state. A local copy is what the removed mount fetch wrote into,
  // and it is the only way the rendered content could diverge from the schema the
  // server emitted from the same value.

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => setIsReady(true), 120);
    });
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, []);

  const scrollToId = useCallback((id: string, updateHash = true) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (updateHash && typeof history !== "undefined") {
      history.replaceState(null, "", `#${id}`);
    }
  }, []);

  const goToCategory = useCallback((id: string) => {
    setActiveGroup(id);
    scrollToId(id);
  }, [scrollToId]);

  const toggleQuestion = useCallback((id: string) => {
    const willOpen = open !== id;
    setOpen(willOpen ? id : null);
    // Deep-link the opened question so the URL can be shared/bookmarked.
    // Runs in the click handler (NOT the setState updater) so it never
    // fires during render — history.replaceState from an event handler is a
    // supported Next App Router pattern for updating the URL without navigating.
    if (willOpen && typeof history !== "undefined") {
      history.replaceState(null, "", `#${id}`);
    }
  }, [open]);

  // On load, honour a #category or #question sub-URL: open + scroll to it.
  // Only ever acts once (`hashHandled`).
  useEffect(() => {
    if (hashHandled.current || typeof window === "undefined") return;
    // ⚠ `decodeURIComponent` THROWS `URIError: URI malformed` on a lone or
    // invalid percent escape — `/faq#100%` and `/faq#%zz` both do it. This runs
    // in an effect, so the throw reached `app/(main)/error.tsx` and blanked the
    // page AFTER the content had painted. Two ways in: a hand-typed or mangled
    // link, and our own `history.replaceState(null, "", `#${id}`)` above, which
    // writes the RAW CMS id — `lib/faq-content.ts` only checks it is a string,
    // so an admin-authored id containing `%` puts the bad hash in the address
    // bar itself. An unreadable deep link must be ignored, never fatal.
    const rawHash = window.location.hash.replace(/^#/, "");
    let hash: string;
    try {
      hash = decodeURIComponent(rawHash).trim();
    } catch {
      hash = rawHash.trim();
    }
    if (!hash) { hashHandled.current = true; return; }

    const group = groups.find((g) => g.id === hash);
    if (group) {
      hashHandled.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- a hash deep-link explicitly controls the active FAQ group after hydration.
      setActiveGroup(group.id);
      requestAnimationFrame(() => scrollToId(group.id, false));
      return;
    }
    for (const g of groups) {
      for (const sec of g.sections) {
        if (sec.faqs.some((f) => f.id === hash)) {
          hashHandled.current = true;
          setActiveGroup(g.id);
          setOpen(hash);
          requestAnimationFrame(() =>
            setTimeout(() => scrollToId(hash, false), 60)
          );
          return;
        }
      }
    }
  }, [groups, scrollToId]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return groups;
    return groups
      .map((group) => ({
        ...group,
        sections: group.sections
          .map((section) => ({
            ...section,
            faqs: section.faqs.filter((faq) =>
              matchesSearch(faq.q, faq.a, q)
            ),
          }))
          .filter((section) => section.faqs.length > 0),
      }))
      .filter((group) => group.sections.length > 0);
  }, [searchQuery, groups]);

  const totalResults = useMemo(() => {
    return filteredGroups.reduce(
      (sum, g) => sum + g.sections.reduce((s, sec) => s + sec.faqs.length, 0),
      0
    );
  }, [filteredGroups]);

  // Track visible group for sidebar highlight (visual only — does not touch the URL)
  useEffect(() => {
    if (!isReady) return;

    let observer: IntersectionObserver | null = null;

    const timeoutId = setTimeout(() => {
      observer = new IntersectionObserver(
        (entries) => {
          const intersecting = entries
            .filter((e) => e.isIntersecting)
            .sort(
              (a, b) =>
                a.boundingClientRect.top - b.boundingClientRect.top
            );
          if (intersecting.length > 0) {
            setActiveGroup(intersecting[0].target.id);
          }
        },
        { rootMargin: "-20% 0px -50% 0px", threshold: 0 }
      );

      filteredGroups.forEach((group) => {
        const el = document.getElementById(group.id);
        if (el) observer!.observe(el);
      });
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [filteredGroups, isReady]);

  // NOTE: do NOT gate this render behind `isReady`/a skeleton — the FAQ copy
  // and hero <h1> must exist in the server-rendered HTML for indexing.
  return (
    <div
      style={{
        animation:
          "faqContentFadeIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards",
      }}
    >
      {/* ─── Hero with Search & Category Navigation ─── */}
      <PageHero
        src="/asset/faq-hero.webp"
        objectPosition="center 42%"
        alt="Florist workspace with scissors, ribbon, and a finished bouquet"
        variant="with-cta"
        ariaLabel="Help Centre"
        subtitle="Help Centre"
        title="Frequently Asked Questions"
        description="Everything you need to know about ordering, delivery, and flower care"
      >
        <div className="mx-auto mt-8 max-w-xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-white/50" />
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="h-12 rounded-xl border-white/20 bg-white/15 pl-12 pr-20 text-white placeholder:text-white/50 shadow-sm backdrop-blur-sm"
              aria-label="Search frequently asked questions"
            />
            {searchQuery && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 text-white/70 hover:text-white hover:bg-white/10"
              >
                Clear
              </Button>
            )}
          </div>

          {searchQuery && (
            <p className="mt-3 text-center text-sm text-white/70" aria-live="polite" aria-atomic="true">
              {totalResults} result{totalResults !== 1 ? "s" : ""} found
            </p>
          )}
        </div>

        {!searchQuery && (
          <div className="mx-auto mt-6 flex flex-wrap justify-center gap-2">
            {groups.map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => goToCategory(group.id)}
                className={`inline-flex min-h-11 items-center rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                  activeGroup === group.id
                    ? "border-white bg-white text-foreground"
                    : "border-white/30 bg-white/10 text-white/80 hover:border-white/50 hover:text-white"
                }`}
              >
                {group.title}
              </button>
            ))}
          </div>
        )}
      </PageHero>

      {/* ─── FAQ Content (questions first) ─── */}
      <section className="bg-background px-6 py-12 md:px-10 md:py-16 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Sidebar — desktop only */}
            <aside
              ref={sidebarRef}
              className="hidden shrink-0 lg:block lg:w-56"
            >
              <nav
                className="sticky top-24 space-y-1"
                aria-label="FAQ categories"
              >
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Topics
                </p>
                {filteredGroups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => goToCategory(group.id)}
                    className={`flex w-full items-center rounded-lg border-l-2 px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                      activeGroup === group.id
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {group.title}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Main content */}
            <div className="min-w-0 flex-1">
              {filteredGroups.length === 0 ? (
                <div className="flex flex-col items-center py-20 text-center">
                  <Search className="h-10 w-10 text-muted-foreground/40" />
                  <p className="mt-4 font-medium text-foreground">
                    No results found
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Try different keywords or{" "}
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="rounded text-primary underline underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      clear your search
                    </button>
                  </p>
                </div>
              ) : (
                <div className="space-y-14">
                  {filteredGroups.map((group, groupIdx) => (
                    <section
                      key={group.id}
                      id={group.id}
                      className="scroll-mt-24 animate-gallery-item"
                      style={{
                        animationDelay: `${Math.min(groupIdx * 0.03, 0.3)}s`,
                      }}
                    >
                      <div className="mb-6 border-b border-border/50 pb-3">
                        <h2 className="heading-section">
                          {group.title}
                        </h2>
                        {group.description && (
                          <p className="mt-1 text-sm text-muted-foreground">
                            {group.description}
                          </p>
                        )}
                      </div>

                      <div className="space-y-8">
                        {group.sections.map((section) => {
                          // Each question is a REAL heading, so a ~50-question
                          // page exposes ~50 outline nodes instead of ~11 —
                          // screen-reader users can jump question to question,
                          // and passage extractors (AI answer engines) get the
                          // segment boundaries the copy always deserved.
                          //
                          // Level is derived, not fixed: the category title is
                          // an <h2> and the optional section sub-title an <h3>,
                          // so a question is h3 normally and h4 beneath a
                          // rendered sub-title. Never h2 — 47 sibling <h2>s
                          // would flatten the categories out of the outline.
                          const showSectionTitle = group.sections.length > 1;
                          const Question = showSectionTitle ? "h4" : "h3";
                          return (
                            <div key={section.id}>
                              {showSectionTitle && (
                                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                  {section.title}
                                </h3>
                              )}
                              <div className="space-y-3">
                                {section.faqs.map((faq) => {
                                  const isOpen = open === faq.id;
                                  return (
                                    <div
                                      key={faq.id}
                                      id={faq.id}
                                      className={`scroll-mt-28 overflow-hidden rounded-xl border bg-background transition-colors ${
                                        isOpen ? "border-primary/40 shadow-sm" : "border-border/50 hover:border-border"
                                      }`}
                                    >
                                      {/* The heading WRAPS the button (the
                                          WAI-ARIA APG accordion pattern). A
                                          heading inside a <button> would be
                                          invalid HTML — button takes phrasing
                                          content only — and assistive tech
                                          flattens button contents into the
                                          accessible name, so the heading would
                                          not be exposed at all. `m-0` keeps the
                                          card pixel-identical. */}
                                      <Question className="m-0">
                                        <button
                                          onClick={() => toggleQuestion(faq.id)}
                                          aria-expanded={isOpen}
                                          aria-controls={`faq-answer-${faq.id}`}
                                          className={`flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-6 ${
                                            isOpen ? "bg-primary/5" : "hover:bg-muted/40"
                                          }`}
                                        >
                                          <span
                                            className={`text-base font-semibold leading-snug ${
                                              isOpen ? "text-primary" : "text-foreground"
                                            }`}
                                          >
                                            {faq.q}
                                          </span>
                                          <span
                                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                                              isOpen
                                                ? "bg-primary text-primary-foreground"
                                                : "bg-muted text-muted-foreground"
                                            }`}
                                            aria-hidden="true"
                                          >
                                            <ChevronDown
                                              className={`h-4 w-4 transition-transform duration-200 ease-out ${
                                                isOpen ? "rotate-180" : ""
                                              }`}
                                            />
                                          </span>
                                        </button>
                                      </Question>
                                      <div
                                        id={`faq-answer-${faq.id}`}
                                        role="region"
                                        hidden={!isOpen}
                                        className="border-t border-border/50 bg-muted/30 px-5 py-4 sm:px-6"
                                      >
                                        <p className="border-l-2 border-primary/40 pl-4 text-[15px] leading-relaxed text-foreground/80">
                                          {faq.a}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── How ordering works (moved below the questions) ─── */}
      <section
        aria-label="How ordering works"
        className="bg-muted px-6 py-12 md:px-10 md:py-16 lg:px-16"
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
              New here?
            </span>
            <h2 className="heading-section mt-3">How ordering works</h2>
          </div>
          <ol className="mt-8 grid gap-6 sm:grid-cols-3">
            {ORDER_STEPS.map((step, idx) => (
              <li
                key={step.title}
                className="rounded-xl border border-border/30 bg-background p-6 text-center"
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <step.icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h3 className="heading-card mt-4">
                  <span className="text-primary">{idx + 1}.</span> {step.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{step.body}</p>
              </li>
            ))}
          </ol>
          <p className="mx-auto mt-6 text-center text-sm text-muted-foreground">
            Same-day delivery within Leeds is available — we&apos;d gently
            encourage ordering ahead where you can for the best choice and full
            customisation.
          </p>
        </div>
      </section>

      {/* ─── Still need help? ─── */}
      <section className="bg-background px-6 py-12 md:px-10 md:py-16 lg:px-16">
        <div className="mx-auto max-w-7xl text-center">
          <h2 className="heading-section">Still have questions?</h2>
          <p className="mt-3 text-muted-foreground">
            Our on-site assistant answers instantly, and a real person always replies on WhatsApp.
          </p>

          {/* Genuine customer proof (rendered only when real testimonials exist) */}
          {customerProof && customerProof.count > 0 && (
            <div className="mx-auto mt-6 max-w-xl rounded-xl border border-border/30 bg-muted/40 p-5">
              <div
                className="flex items-center justify-center gap-1 text-primary"
                aria-label={
                  customerProof.count >= MIN_PROOF_COUNT
                    ? `Rated ${customerProof.average.toFixed(1)} out of 5 by ${customerProof.count} customers`
                    : `Rated ${customerProof.average.toFixed(1)} out of 5`
                }
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${i < Math.round(customerProof.average) ? "fill-current" : "opacity-30"}`}
                    aria-hidden="true"
                  />
                ))}
                <span className="ml-2 text-sm font-medium text-foreground">
                  {customerProof.count >= MIN_PROOF_COUNT
                    ? `${customerProof.average.toFixed(1)} from ${customerProof.count} happy customers`
                    : `Rated ${customerProof.average.toFixed(1)} out of 5`}
                </span>
              </div>
              {customerProof.quote && (
                <blockquote className="mt-3 text-sm italic text-muted-foreground">
                  &ldquo;{customerProof.quote.text}&rdquo;
                  <footer className="mt-1 text-xs font-medium not-italic text-foreground">
                    &mdash; {customerProof.quote.author}
                  </footer>
                </blockquote>
              )}
            </div>
          )}
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              asChild
              variant="default"
              size="lg"
              className="w-full sm:w-auto sm:min-w-48"
            >
              <Link href="/enquire">
                <MessageCircle className="h-4 w-4" />
                Send us a message
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="w-full sm:w-auto sm:min-w-48"
            >
              <Link href="/products">
                <ShoppingBag className="h-4 w-4" />
                Browse products
              </Link>
            </Button>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            You can also read our{" "}
            <ProseLink href="/flower-care">flower care guide</ProseLink>,{" "}
            learn about{" "}
            <ProseLink href="/flower-delivery-leeds">Leeds delivery areas</ProseLink>,{" "}
            or explore our{" "}
            <ProseLink href="/gallery">gallery</ProseLink>.
          </p>
        </div>
      </section>
    </div>
  );
}
